import React, { type ComponentType, Fragment, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions';
import { Box } from '@atlaskit/primitives/compiled';
import { WidthObserver } from '@atlaskit/width-detector';
import {
	CornerStyle,
	ExtensionMode,
	HubExtensionsWrapper,
	type ProductDependenciesProviderProps,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import { ImageRenderer } from '@atlassian/company-hub-common/media-components';
import { SpotlightContent } from '@atlassian/company-hub-common/spotlight-content';
import {
	SpotlightLayout,
	VERTICAL_MAX_WIDTH,
} from '@atlassian/company-hub-common/spotlight-layout';

import type { SpotlightParameters } from '../../../../types';

type ImageSource = SpotlightParameters['content']['image']['imageSrc'];

const getImage = (src: ImageSource, contentId: string, alt?: string) => {
	return src ? (
		<ImageRenderer
			contentId={contentId}
			imageSrc={src}
			imageAlt={alt}
			shouldShowExternalSiteImage
		/>
	) : null;
};

const styles = cssMap({
	containerStyles: {
		position: 'relative',
		overflow: 'hidden',
	},
	previewStyles: { pointerEvents: 'none' },
});

export type SpotlightContainerProps = {
	contentId?: string;
	extensionNode?: ExtensionParams<SpotlightParameters>;
	isInEditor?: boolean;
	isPreviewSize?: boolean;
	PropsProvider?: ComponentType<ProductDependenciesProviderProps>;
};

export const SpotlightBase = ({
	extensionNode,
	contentId = '',
	isPreviewSize,
	isInEditor = false,
}: Omit<SpotlightContainerProps, 'PropsProvider'>) => {
	const { experienceStartComponent, isCompanyHub } = useProductDependencies();
	const ExperienceStart = experienceStartComponent || Fragment;
	const [width, setWidth] = useState(VERTICAL_MAX_WIDTH + 1);
	const { parameters } = extensionNode || {};

	if (!parameters) {
		return <Box testId="no-param-state" />;
	}
	const { backgroundColor, content, height, layout, cornerStyle } = parameters;
	const {
		text: { title, description, textColor },
		button: { isButtonShown, text: buttonText, link, style: buttonStyle },
	} = content;

	// we only offer customized corner style on Company Hub, otherwise it defaults to round
	const useRoundedCorners = cornerStyle === CornerStyle.ROUNDED || !isCompanyHub;

	return (
		<HubExtensionsWrapper<SpotlightParameters>
			mode={ExtensionMode.RENDERER}
			extensionNode={extensionNode}
			contentId={contentId}
			showNoDataState={!parameters}
			packageName="spotlight-extension"
		>
			<ExperienceStart />
			<Box
				xcss={cx(styles.containerStyles, isPreviewSize && styles.previewStyles)}
				// tiny bit of padding here in edit mode helps user to see their
				// squared corner style, since the extension container border radius
				// cuts it off
				paddingBlock={isInEditor && !useRoundedCorners ? 'space.050' : undefined}
				testId={`spotlight-${useRoundedCorners ? 'rounded' : 'squared'}`}
			>
				{!isPreviewSize && <WidthObserver setWidth={setWidth} offscreen />}
				<SpotlightLayout
					testId="spotlight-extension"
					backgroundColor={backgroundColor}
					height={height}
					layout={layout}
					image={getImage(content.image.imageSrc, contentId, content.image.altText)}
					width={width}
					hasRoundedCorners={useRoundedCorners}
				>
					<SpotlightContent
						layout={layout}
						title={title}
						description={description}
						textColor={textColor}
						isButtonShown={isButtonShown}
						buttonText={buttonText}
						link={link}
						buttonStyle={buttonStyle}
						width={width}
						source="spotlightExtension"
					/>
				</SpotlightLayout>
			</Box>
		</HubExtensionsWrapper>
	);
};

const SpotlightWithProvider = (props: SpotlightContainerProps) => {
	const { extensionNode, contentId = '', isInEditor, PropsProvider = Fragment } = props;

	return (
		<PropsProvider
			mode={isInEditor ? ExtensionMode.EDITOR : ExtensionMode.RENDERER}
			contentId={contentId}
			extensionNode={extensionNode}
		>
			<SpotlightBase {...props} />
		</PropsProvider>
	);
};

export const Spotlight = SpotlightWithProvider;
